import type { LifespanStep } from '../_common/types.js';

export enum SessionOrderBy {
  CreatedAt = 'createdAt',
  UpdatedAt = 'updatedAt',
}

export enum SessionDbOrderBy {
  CreatedAt = 'created_at',
  UpdatedAt = 'updated_at',
}

export enum SessionArchivalFilter {
  ActiveOnly = 'active_only',
  ArchivedOnly = 'archived_only',
  Any = 'any',
}

/** session.status */
export enum SessionStatus {
  Pending = 'Pending',
  Live = 'Live',
  Halted = 'Halted',
}

/**
 * Closed set of entity kinds that have a per-session short-id and are
 * referenceable by tools, prompts, and FE rendering. The string values
 * are the lowercase prefix of the canonical short-id form (e.g. `product`
 * → `p#`, `version` → `v#`).
 */
export enum SessionEntityKind {
  Product = 'product',
  Version = 'version',
  Collab = 'collab',
  Reflection = 'reflection',
  Remark = 'remark',
  File = 'file',
  Member = 'member',
  User = 'user',
}

/** session.error */
export enum SessionError {
  ModelError = 'ModelError',
  RetryLimitReached = 'RetryLimitReached',
  ContextRequestsExhausted = 'ContextRequestsExhausted',
  TranslationError = 'TranslationError',
  ConsumptionError = 'ConsumptionError',
  LeaseLost = 'LeaseLost',
  InfrastructureError = 'InfrastructureError',
  UnknownError = 'UnknownError',
  StalledError = 'StalledError',
  InsufficientCredits = 'InsufficientCredits',
}

/**
 * Which signal condemned a Live session as stalled.
 *
 * `LeaseExpired` means the runner held a lease and stopped renewing it — the
 * row survived its owner, so the owner died without releasing (a killed
 * process, a lost box). `LeaseMissing` means the session sat Live past
 * `SESSION_STALL_TIMEOUT_MS` with no lease row at all. `ProgressStalled` is the
 * live wedge: the owner is alive and renewing on schedule, but the run itself
 * stopped moving — the only signal whose holder must be evicted rather than
 * merely mourned.
 */
export enum StallSignal {
  LeaseExpired = 'leaseExpired',
  LeaseMissing = 'leaseMissing',
  ProgressStalled = 'progressStalled',
}

/** session.halt_reason values */
export enum HaltReason {
  Error = 'error',
  QuestionToUser = 'questionToUser',
  Concluded = 'concluded',
  NoMoreRounds = 'noMoreRounds',
  UserHalt = 'userHalt',
  InsufficientTokens  = 'InsufficientTokens',
}

/** Session runtime step name (used in llm_request and session.halted_on_step) */
export enum Step {
  Bootstrap = 'bootstrap',
  Reflect = 'reflect',
  PlanAssign = 'plan_assign',
  Write = 'write',
  PeerReview = 'peer_review',
  Inspection = 'inspect',
  Presentation = 'present',
}

/** The next position plus whether that transition turned the round over. */
export interface StepTransition extends LifespanStep {
  readonly crossedBoundary: boolean;
}

const STEP_SUCCESSOR: Record<Step, Step> = {
  [Step.Bootstrap]: Step.Presentation,
  [Step.Reflect]: Step.PlanAssign,
  [Step.PlanAssign]: Step.Write,
  [Step.Write]: Step.PeerReview,
  [Step.PeerReview]: Step.Inspection,
  [Step.Inspection]: Step.Presentation,
  [Step.Presentation]: Step.Reflect,
};

/**
 * The single source of truth for "what comes next" — successor step, next round,
 * and whether the round turned over. Replaces nextStep + isRoundBoundary.
 * `Bootstrap` succeeds to the round-0 warm-up Presentation (not a boundary); the
 * round-0 warm-up Presentation succeeds to round-1 Write; every other Presentation
 * closes its round into the next Reflect.
 */
export function advance({ round, step }: LifespanStep): StepTransition {
  if (step === Step.Presentation && round === 0) return { round: 1, step: Step.Write, crossedBoundary: true };
  if (step === Step.Presentation) return { round: round + 1, step: Step.Reflect, crossedBoundary: true };
  return { round, step: STEP_SUCCESSOR[step], crossedBoundary: false };
}

/** session_path.origin */
export enum SessionPathOrigin {
  Root = 'root',
  Fork = 'fork',
  Restore = 'restore',
  /**
   * Synthetic path that holds saved-team template state. One per saved Team.
   * Has no Session and no parent — exists only so path-scoped state attached
   * to a saved team (its members, today; potentially other team-scoped state
   * later) can keep the same composite (id, path_id) PK as session paths.
   */
  Template = 'template',
}

export enum TravelMode {
  Visit = 'visit',
  Travel = 'travel',
  Fork = 'fork',
  Override = 'override',
  Savepoint = 'savepoint',
}

/**
 * Member activity state constant.
 * Used when a member is not currently executing any step.
 */
export const MEMBER_STEP_IDLE = 'idle';

/** session.access */
export enum Access {
  Public = 'public',
  Private = 'private',
}

/** repository.type - ownership type */
export enum RepositoryType {
  Personal = 'personal',  // Owned by single user
  Legion = 'legion',      // Shared with legion members
}

/** Identifies who made an LLM call (llm_request.caller and orchestration context) */
export enum CallerKind {
  System = 'system',
  Chair = 'chair',
  Watchdog = 'watchdog',
  Envoy = 'envoy',
  Operative = 'operative',
}

/** Standard keys for member_session_data table (dynamic session-specific data) */
export enum MemberSessionDataKey {
  CustomPersonaPrompt = 'custom_persona_prompt',
  OperativeResponsibility = 'operative_responsibility',
  OperativeArea = 'operative_area',
}

/** Halt types for user interaction */
export enum HaltType {
  Question = 'question',
  Done = 'done',
}

/** Session run intent accepted by orchestration endpoint */
export enum RunningIntent {
  Run = 'run',
  Stop = 'stop',
  None = 'none',
}

/** Outcome returned by orchestration intent endpoint */
export enum IntentOutcome {
  RunSuccess = 'runSuccess',
  AlreadyRunning = 'alreadyRunning',
  HaltSuccess = 'haltSuccess',
  NoStatusChange = 'noStatusChange',
  // Run refused by the restart breaker: the session halted too many consecutive times
  // without a pointer advance. It stays Halted with its haltReason intact instead of
  // flipping Live into a doomed execution; `force: true` on the intent overrides.
  RestartSuppressed = 'restartSuppressed',
  Errored = 'errored',
}

/** Sessions list ordering fields for API query params */
export enum SessionListOrderBy {
  CreatedAt = 'createdAt',
  UpdatedAt = 'updatedAt',
}

/** Author discriminator for session intent collab payload */
export enum SessionIntentAuthorType {
  Member = 'member',
  User = 'user',
}

// ============================================================================
// Handy "values" constants (useful for OpenAPI or Zod)
// ============================================================================

export const STEP_VALUES = Object.freeze(Object.values(Step));

export const MEMBER_SESSION_DATA_KEY_VALUES = Object.freeze(Object.values(MemberSessionDataKey));

export const SESSION_STATUS_VALUES = Object.freeze(Object.values(SessionStatus));

export const SESSION_ERROR_VALUES = Object.freeze(Object.values(SessionError));

export const HALT_REASON_VALUES = Object.freeze(Object.values(HaltReason));

export const ACCESS_VALUES = Object.freeze(Object.values(Access));

export const CALLER_KIND_VALUES = Object.freeze(Object.values(CallerKind));

export const HALT_TYPE_VALUES = Object.freeze(Object.values(HaltType));

export const REPOSITORY_TYPE_VALUES = Object.freeze(Object.values(RepositoryType));

export const RUNNING_INTENT_VALUES = Object.freeze(Object.values(RunningIntent));

export const INTENT_OUTCOME_VALUES = Object.freeze(Object.values(IntentOutcome));

export const SESSION_LIST_ORDER_BY_VALUES = Object.freeze(Object.values(SessionListOrderBy));

export const SESSION_INTENT_AUTHOR_TYPE_VALUES = Object.freeze(Object.values(SessionIntentAuthorType));
